import {
  TagadaError,
  TagadaAuthError,
  TagadaValidationError,
  TagadaNotFoundError,
  TagadaRateLimitError,
  TagadaNetworkError,
} from './errors.js';
import { SDK_VERSION } from './version.js';
import type { Environment, HttpRequestOptions } from './types.js';

const BASE_URLS: Record<Environment, string> = {
  production: 'https://api.tagada.io',
  development: 'https://api.tagada.dev',
  local: 'http://localhost:3000',
};

export interface HttpClientConfig {
  baseUrl: string;
  timeout: number;
  apiKey?: string;
  getToken?: () => string | undefined;
  redirect?: RequestRedirect;
}

/**
 * Lightweight HTTP client built on native fetch.
 * Zero dependencies -- works in browser, edge runtimes, and Node 18+.
 */
export class HttpClient {
  readonly baseUrl: string;
  private timeout: number;
  private apiKey?: string;
  private getToken?: () => string | undefined;
  private redirect?: RequestRedirect;

  constructor(config: HttpClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, '');
    this.timeout = config.timeout;
    this.apiKey = config.apiKey;
    this.getToken = config.getToken;
    this.redirect = config.redirect;
  }

  static resolveBaseUrl(environment: Environment, override?: string): string {
    return override ?? BASE_URLS[environment] ?? BASE_URLS.production;
  }

  async request<T = unknown>(path: string, options: HttpRequestOptions = {}): Promise<T> {
    const { method = 'GET', body, headers = {}, params, token, signal } = options;

    let url = `${this.baseUrl}${path}`;
    if (params) {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined) qs.set(k, String(v));
      }
      const str = qs.toString();
      if (str) url += `?${str}`;
    }

    const reqHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      // Which SDK and version sent the call: the Payments API readiness check counts them per store.
      'X-Tagada-SDK': `headless-sdk/${SDK_VERSION}`,
      ...headers,
    };

    const authToken = token ?? this.apiKey ?? this.getToken?.();
    if (authToken) {
      reqHeaders['x-cms-token'] = authToken;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const res = await fetch(url, {
        method,
        headers: reqHeaders,
        body: body != null ? JSON.stringify(body) : undefined,
        signal: signal ?? controller.signal,
        ...(this.redirect && { redirect: this.redirect }),
      });

      clearTimeout(timeoutId);

      if (res.ok) {
        const text = await res.text();
        return text ? (JSON.parse(text) as T) : ({} as T);
      }

      await this.handleErrorResponse(res, path);
      throw new TagadaError('Unexpected error', 'unknown', res.status);
    } catch (err) {
      clearTimeout(timeoutId);
      if (err instanceof TagadaError) throw err;
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new TagadaNetworkError(`Request to ${path} timed out after ${this.timeout}ms.`);
      }
      throw new TagadaNetworkError(
        err instanceof Error ? err.message : 'Network request failed.',
      );
    }
  }

  get<T = unknown>(path: string, options?: HttpRequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  post<T = unknown>(path: string, body?: unknown, options?: HttpRequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST', body });
  }

  patch<T = unknown>(path: string, body?: unknown, options?: HttpRequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PATCH', body });
  }

  put<T = unknown>(path: string, body?: unknown, options?: HttpRequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PUT', body });
  }

  delete<T = unknown>(path: string, options?: HttpRequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }

  private async handleErrorResponse(res: Response, path: string): Promise<never> {
    let parsed: Record<string, unknown> = {};
    try {
      parsed = (await res.json()) as Record<string, unknown>;
    } catch {
      // response isn't JSON
    }

    // Server actions answer `{ code, data }`, where `data` carries the
    // customer-facing message and an application error code — `Error.message`
    // is non-enumerable, so it cannot travel at the top level (#TAG-1989).
    // Without this the buyer sees "Request failed: 422 …" instead of the
    // reason, and the code the storefront should branch on is thrown away.
    // The REST handler sends an object `data` as a JSON string, with an empty `message`.
    const data = parseErrorData(parsed.data);

    const msg =
      (parsed.message as string) ||
      (parsed.error as string) ||
      (typeof data.message === 'string' ? data.message : undefined) ||
      (typeof parsed.data === 'string' && Object.keys(data).length === 0 ? (parsed.data as string) : undefined) ||
      `Request failed: ${res.status} ${res.statusText} on ${path}`;

    const appCode = typeof data.code === 'string' ? data.code : undefined;

    switch (res.status) {
      case 401:
      case 403:
        throw new TagadaAuthError(msg);
      case 404:
        throw new TagadaNotFoundError(path, '');
      case 422: {
        const fieldErrors = Array.isArray(parsed.errors)
          ? (parsed.errors as Array<{ field: string; message: string }>)
          : [];
        throw new TagadaValidationError(msg, fieldErrors, appCode, parsed);
      }
      case 429: {
        const retryAfter = Number(res.headers.get('Retry-After') ?? '1') * 1000;
        throw new TagadaRateLimitError(retryAfter);
      }
      default:
        throw new TagadaError(msg, 'api_error', res.status, parsed);
    }
  }
}

function parseErrorData(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object') return value as Record<string, unknown>;
  if (typeof value !== 'string') return {};
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}
